import React, { useMemo } from "react";

import { Box, Theme, Typography, useTheme } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { format } from "date-fns/esm";
import { camelCase, endsWith, get } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { DeleteIconButton, EditIconButton } from "@/components/IconButtons";
import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { percent } from "@/utils/number";
import { FragmentType } from "@/utils/type";

interface BodyCompositionDetailsProp {
  date: string;
  bodyCompositions?: FragmentType<typeof bodyCompositionDetailsFragment>;
  onEdit: (date: Date) => void;
  onDelete: () => void;
  settings: FragmentType<typeof bodyCompositionDetailsSettingFragment>;
}

const useStyles = makeStyles()((theme: Theme) => ({
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    width: "10%",
  },
  fieldsContainer: {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid #D4DBE5",
    marginRight: theme.spacing(8),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    width: "20%",
  },
  fieldsText: {
    alignSelf: "center",
    marginLeft: theme.spacing(2),
    fontWeight: "bold",
  },
  fieldsLabel: {
    fontSize: theme.typography.pxToRem(14),
    alignSelf: "center",
    width: "60%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: "bold",
  },
  summaryItem: {
    backgroudColor: colors.white,
  },
}));

const StyledAccordion = withStyles(Accordion, (theme: Theme) => ({
  root: {
    backgroundColor: colors.light,
    padding: 0,
    boxShadow: "none",
    marginTop: theme.spacing(4),
  },
}));

const StyledAccordionSummary = withStyles(AccordionSummary, {
  root: {
    padding: "0px 20px",
  },
});

const StyledAccordionDetails = withStyles(AccordionDetails, {
  root: {
    backgroundColor: colors.white,
    display: "flex",
    flexDirection: "row-reverse",
    padding: "10px 0px",
  },
});

const bodyCompositionDetailsFragment = graphql(`
  fragment BodyCompositionDetails on BodyCompositionGrouped {
    bodyFatPercentage
    boneMassKg
    fatFreeMassKg
    fatFreeMassPercentage
    fatMassKg
    leanBodyMassKg
    leanBodyMassPercentage
    metabolicAge
    muscleMassKg
    muscleMassPercentage
    visceralFatKg
    visceralFatPercentage
    bodyCellMassKg
    bodyWaterPercentage
    boneMassPercentage
    extraCellularWaterLiter
    extraCellularWaterPercentage
    intraCellularWaterLiter
    intraCellularWaterPercentage
    residualMassKg
    residualMassPercentage
    totalBodyWaterLiter
    createdAt
    date
  }
`);

const bodyCompositionDetailsSettingFragment = graphql(`
  fragment BodyCompositionDetails_Setting on Setting {
    id
    bodyCompositionFields {
      fieldLabel
      fieldName
      visible
    }
  }
`);

const BodyCompositionDetails: React.FC<BodyCompositionDetailsProp> = ({
  bodyCompositions,
  settings,
  onEdit,
  date,
  onDelete,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const bodyCompositionTime = bodyCompositions?.createdAt
    ? format(new Date(bodyCompositions?.createdAt), "hh:mm bb")
    : "-";
  const dateCreated = format(new Date(date), "dd MMM, yyyy");

  const getFieldValue = (
    fieldName: string,
    value: number | null
  ): string | number | null => {
    if (value === null) return null;
    if (endsWith(fieldName, "Percentage")) {
      return `${percent(value, { round: 2 })} %`;
    }
    return value;
  };

  const displayFields = useMemo(
    () => () => {
      if (settings && settings?.bodyCompositionFields) {
        return (
          <Box display="flex" flexWrap="wrap">
            {settings.bodyCompositionFields.map((field, index) => {
              const key = camelCase(field.fieldName);
              const value = get(bodyCompositions, key);

              if (value !== null) {
                return (
                  <div className={classes.fieldsContainer} key={index}>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      className={classes.fieldsLabel}
                    >
                      {field.fieldLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textPrimary"
                      className={classes.fieldsText}
                    >
                      {getFieldValue(key, value)}
                    </Typography>
                  </div>
                );
              } else if (field.visible) {
                const fieldValue = getFieldValue(key, value);
                return (
                  <div className={classes.fieldsContainer} key={index}>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      className={classes.fieldsLabel}
                    >
                      {field.fieldLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textPrimary"
                      className={classes.fieldsText}
                    >
                      {fieldValue === null ? "-" : fieldValue}
                    </Typography>
                  </div>
                );
              }
            })}
          </Box>
        );
      }
    },
    [settings?.bodyCompositionFields, bodyCompositions]
  );

  return (
    <>
      <StyledAccordion expanded>
        <StyledAccordionSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box flex={1} display="flex" alignItems="center">
            <Box flex={1} display="flex">
              <Box
                marginRight={theme.spacing(3)}
                display="flex"
                flexDirection="column"
                flexShrink={1}
              >
                <Typography className={classes.heading}>
                  {dateCreated}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bodyCompositionTime}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              <Box marginX={1} />
              <EditIconButton onClick={() => onEdit(new Date(date))} />
              <Box marginX={1} />
              <DeleteIconButton onClick={onDelete} />
            </Box>
          </Box>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Box display="flex" flexWrap="wrap">
            {displayFields()}
          </Box>
        </StyledAccordionDetails>
      </StyledAccordion>
    </>
  );
};

export default BodyCompositionDetails;
